import React, { useState } from 'react'
import { Link } from 'gatsby'
import Navbar, { MenuButton } from './Navbar'
import LinkButton from './LinkButton'
import useSVGPublicURL from '../ui/utils/useSVGPublicURL'
import { RENTAL_APPLICATION_URLS } from '../rentalApplication/constants'

interface HeaderProps {
  isHero?: boolean;
  title?: string;
}

/**
 * Shared header component for both Hero and other pages like Events
 */
const Header: React.FC<HeaderProps> = ({ isHero = false, title }) => {
  const CuldesacLogo = useSVGPublicURL('culdesac-logo-full')
  // Track mobile menu state here at the header level
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Toggle the mobile menu state
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  // Mobile header
  const MobileHeader = () => (
    <div className="md:hidden flex flex-col items-start w-full z-10">
      {/* Fixed top row: hamburger and logo remain aligned regardless of menu state */}
      <div className="w-full flex items-center justify-between">
        {/* Left side: Hamburger menu toggle button with logo */}
        <div className="flex items-center">
          <div className="pl-3 xs:pl-[0.9rem] hamburger-theme-white flex items-center">
            <MenuButton 
              isMenuOpen={mobileMenuOpen} 
              toggleMenu={toggleMobileMenu} 
            />
          </div>
        
          {/* Logo always visible and aligned with hamburger (for non-hero pages) */}
          {!isHero && (
            <div className="flex items-center">
              <Link to="/">
                <img
                  alt="Culdesac logo"
                  loading="eager"
                  src={CuldesacLogo}
                  className="h-7 w-auto object-contain ml-3 -mt-[3px]"
                />
              </Link>
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile menu content only renders when open */}
      {mobileMenuOpen && (
        <div className="w-full">
          <Navbar 
            isHero={isHero} 
            menuContentOnly={true} 
          />
        </div>
      )}
      
      {/* For hero pages: show big logo - below menu when open, or right after hamburger when closed */}
      {isHero && (
        <div className={`flex items-start gap-3 ${mobileMenuOpen ? 'mt-8' : 'mt-4'}`}>
          <img
            alt="Culdesac logo"
            loading="eager"
            src={CuldesacLogo}
            className="w-auto object-contain"
          />
        </div>
      )}
    </div>
  )

  // Desktop header
  const DesktopHeader = () => (
    <div className="hidden md:flex flex-col relative z-10 w-full">
      {/* Top Row: Navigation & Explore Button - ensure consistent spacing */}
      <div className="flex items-center justify-between w-full py-12 px-24">
        <div className={`flex flex-1 items-center gap-10 ${!isHero ? 'mr-32' : ''}`}>
          {!isHero && (
            <Link to="/">
              <img
                alt="Culdesac logo"
                loading="eager"
                src={CuldesacLogo}
                className="h-6 w-auto object-contain"
              />
            </Link>
          )}
          <Navbar isHero={isHero} />
        </div>
        <div className="ml-16">
          <LinkButton 
            variant="transparent" 
            url={RENTAL_APPLICATION_URLS.UNIT_SELECTION_URL} 
          >
            EXPLORE FLOOR PLANS
          </LinkButton>
        </div>
      </div>

      {/* Logo row (only for Hero) */}
      {isHero && (
        <div className="mx-auto">
          <img
            alt="Culdesac logo"
            loading="eager"
            src={CuldesacLogo}
            height={161}
            className="h-[161px] object-top object-contain"
          />
        </div>
      )}

      {/* Title row (for non-Hero pages) */}
      {!isHero && title && (
        <div className="w-full bg-new-white py-8 md:py-12 text-black">
          <h1 className="text-4xl md:text-6xl font-bold text-center font-new-sans-black">
            {title}
          </h1>
        </div>
      )}
    </div>
  )

  return (
    <>
      <MobileHeader />
      <DesktopHeader />
    </>
  )
}

export default Header 